import type { SegmentFilterField, SegmentFilterRule } from '@carbonwebmail/domain'
import { operatorsFor, ruleFor } from '@carbonwebmail/domain'
import { useState } from 'react'
import { FilterActions } from '../../ui/FilterActions.js'
import { Popover } from '../../ui/Popover.js'
import { boardTool } from '../../ui/toolButton.js'
import type { KeyedSegmentRule } from './useSegments.js'

/**
 * Narrowing the segments board by what a segment *is*.
 *
 * **Not the contacts filter and not a copy of it.** That one asks about people
 * and becomes SQL; this asks about segments — what one is called, how many
 * rules it has, what it asks about, when it changed — and is applied in the
 * browser to a list already in hand. The vocabulary is
 * `packages/domain/src/model/segmentFilter.ts`, which is also what a saved view
 * stores and the server validates.
 *
 * A flat list, all of which must hold. No groups and no "any of": the contacts
 * board's filter is the same shape and the owner confirmed it stays that way.
 *
 * **Changing a rule's field resets its operator and value**, through `ruleFor`,
 * because most operators mean nothing to most fields — that is the bug the
 * contacts board shipped, where switching to a days field left it comparing
 * with `is` and showing nobody.
 */

/** What each field is called, in the order somebody would look for them. */
const FIELDS: readonly (readonly [SegmentFilterField, string])[] = [
  ['name', 'Name'],
  ['asks_about', 'Asks about'],
  ['rules', 'Number of rules'],
  ['changed', 'Days since changed'],
  ['created', 'Days since created'],
]

/** Plain words, because an enum read aloud is not a sentence. */
const OPERATOR_WORDS: Record<string, string> = {
  is: 'is',
  is_not: 'is not',
  contains: 'contains',
  not_contains: 'does not contain',
  at_least: 'at least',
  at_most: 'at most',
}

export function SegmentFilterControl({
  rules,
  onRules,
  onNewRule,
  onSaveAsView,
}: {
  readonly rules: readonly KeyedSegmentRule[]
  readonly onRules: (rules: readonly KeyedSegmentRule[]) => void
  /** Mints a rule with an identity, so a row keeps its own as the list changes. */
  readonly onNewRule: (rule: SegmentFilterRule) => KeyedSegmentRule
  readonly onSaveAsView: () => void
}) {
  const [open, setOpen] = useState(false)

  return (
    <Popover
      align="right"
      label="Filter"
      onClose={() => setOpen(false)}
      onToggle={() => setOpen((was) => !was)}
      open={open}
      panelClassName="w-[26rem] p-3"
      trigger={rules.length === 0 ? 'Filter' : `Filter (${rules.length})`}
      triggerClassName={boardTool(rules.length > 0)}
    >
      <p className="mb-2 font-semibold text-[12px] text-t2 uppercase tracking-[0.06em]">
        Show segments where
      </p>

      {rules.map((one) => (
        <Row
          key={one.key}
          onChange={(next) =>
            onRules(rules.map((each) => (each.key === one.key ? { ...each, rule: next } : each)))
          }
          onRemove={() => onRules(rules.filter((each) => each.key !== one.key))}
          rule={one.rule}
        />
      ))}

      <FilterActions
        count={rules.length}
        onAdd={() => onRules([...rules, onNewRule(ruleFor('name'))])}
        onClear={() => onRules([])}
        onSaveAsView={onSaveAsView}
      />
    </Popover>
  )
}

function Row({
  rule,
  onChange,
  onRemove,
}: {
  readonly rule: SegmentFilterRule
  readonly onChange: (rule: SegmentFilterRule) => void
  readonly onRemove: () => void
}) {
  const numeric = rule.field === 'rules' || rule.field === 'created' || rule.field === 'changed'

  return (
    <span className="mb-2 flex items-center gap-1">
      <select
        aria-label="Field"
        className="min-w-0 flex-1 rounded-field border border-line bg-surface px-2 py-1.5 text-[12px] text-t1"
        onChange={(event) => onChange(ruleFor(event.target.value as SegmentFilterField))}
        value={rule.field}
      >
        {FIELDS.map(([field, label]) => (
          <option key={field} value={field}>
            {label}
          </option>
        ))}
      </select>

      <select
        aria-label="Condition"
        className="rounded-field border border-line bg-surface px-2 py-1.5 text-[12px] text-t1"
        onChange={(event) =>
          onChange({ ...rule, operator: event.target.value as SegmentFilterRule['operator'] })
        }
        value={rule.operator}
      >
        {operatorsFor(rule.field).map((operator) => (
          <option key={operator} value={operator}>
            {OPERATOR_WORDS[operator] ?? operator}
          </option>
        ))}
      </select>

      <input
        aria-label="Value"
        className="min-w-0 flex-1 rounded-field border border-line bg-surface px-2 py-1.5 text-[12px] text-t1"
        // A days or a count field takes a number and says so, which is what
        // puts the right keyboard on a phone.
        inputMode={numeric ? 'numeric' : 'text'}
        onChange={(event) => onChange({ ...rule, value: event.target.value })}
        placeholder={numeric ? '0' : ''}
        type={numeric ? 'number' : 'text'}
        value={rule.value}
      />

      <button
        aria-label="Remove this condition"
        className="rounded-field px-1.5 py-1 text-[13px] text-t3 hover:bg-line-2 hover:text-t1"
        onClick={onRemove}
        type="button"
      >
        ×
      </button>
    </span>
  )
}
